import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { vipAPI } from '../../../services/api';
import { useBrand } from '../../../context/BrandContext';
import { Spinner } from '../../ui/spinner';
import { Alert, AlertDescription } from '../../ui/alert';
import Pager from './Pager';
import QueueTable from './QueueTable';
import { SavedToast } from './ContactLogPanel';
import { QueueBuckets, QueueCoverage, QueueToolbar } from './QueueControls';
import { apiError, PAGE_SIZE } from './trackerView';
import { nextQueuePlayer, queueFiltersFromSearch, queueQuery, searchWithQueueFilters } from './queueView';

const SEARCH_DEBOUNCE_MS = 300;
const TOAST_MS = 6000;

// useQueueData loads the agent's queue for the filters; reload refetches after a save.
function useQueueData(agent, queryKey, search) {
  const { activeBrand } = useBrand();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!agent) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    const filters = queueFiltersFromSearch(new URLSearchParams(queryKey));
    vipAPI.getContactTracker(agent, queueQuery(filters, search), controller.signal)
      .then((res) => setData(res.data))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err.response?.status === 503 ? 'Data source unavailable' : apiError(err, 'Failed to load the queue.'));
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [agent, activeBrand, queryKey, search, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

// DueQueue is the VIP manager's contact queue, ranked by who most needs a contact: overdue
// follow-ups, then never contacted, each by NGR. A contact is logged inside the row; saving closes
// it. "Work the queue" is a mode: saving opens the next player to contact until it is stopped. renderHeader places the coverage beside the title.
export default function DueQueue({ agent, renderHeader }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = queueFiltersFromSearch(searchParams);
  const queryKey = searchWithQueueFilters(filters).toString();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const { data, loading, error, reload } = useQueueData(agent, queryKey, debouncedSearch);
  const [openId, setOpenId] = useState(null);
  const [toast, setToast] = useState('');
  // openAfter: "Work the queue" opens the next due player once the queue reloads (data differs
  // from stale), after the one that was open.
  const [openAfter, setOpenAfter] = useState(null);
  // working: "Work the queue" mode; saving opens the next player until it is stopped.
  const [working, setWorking] = useState(false);
  const firstSearch = useRef(true);
  const toastTimer = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  const setFilters = useCallback((patch) => {
    const next = { ...queueFiltersFromSearch(searchParams), page: 1, ...patch };
    setOpenId(null);
    setSearchParams(searchWithQueueFilters(next), { replace: true });
  }, [searchParams, setSearchParams]);

  // A new search starts from the first page.
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return; }
    if (filters.page !== 1) setFilters({ page: 1 });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearTimeout(toastTimer.current), []);

  const showToast = (text) => {
    setToast(text);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), TOAST_MS);
  };

  useEffect(() => {
    if (!openAfter || loading || !data || data === openAfter.stale) return;
    const next = nextQueuePlayer(data.data || [], openAfter.after);
    setOpenAfter(null);
    if (next) {
      setOpenId(next.user_id);
      return;
    }
    setWorking(false);
    showToast(`${openAfter.toast ? `${openAfter.toast} · ` : ''}Queue clear — everyone due on this page was contacted today.`);
  }, [openAfter, loading, data]); // eslint-disable-line react-hooks/exhaustive-deps

  // openNext moves to page 1 of the due queue and, once it reloads, opens the next player after
  // `after` (toast: the saved contact, kept when the queue is clear).
  const openNext = (after, toast = '') => {
    setOpenAfter({ stale: data, after, toast });
    if (filters.state !== 'due' || filters.page !== 1) setFilters({ state: 'due', page: 1 });
    else reload();
  };

  // leaveWorking ends the mode and drops a pending "open the next player".
  const leaveWorking = () => {
    setWorking(false);
    setOpenAfter(null);
  };

  const stopWorking = () => {
    leaveWorking();
    setOpenId(null);
  };

  const startWorking = () => {
    setWorking(true);
    openNext(openId);
  };

  // Filters, pages and a row's own Log button leave the "Work the queue" mode.
  const changeFilters = (patch) => {
    leaveWorking();
    setFilters(patch);
  };

  const onLog = (userId) => {
    leaveWorking();
    setOpenId(userId);
  };

  // A saved contact closes the form and the queue reloads; in "Work the queue" mode the next
  // player opens.
  const onSaved = (row, { toast: text }) => {
    showToast(text);
    setOpenId(null);
    if (working) openNext(row.user_id, text);
    else reload();
  };

  const summary = data?.summary;
  const rows = data?.data || [];
  const header = renderHeader(
    <QueueCoverage
      summary={summary} working={working} disabled={!working && !summary?.due_now}
      onWork={working ? stopWorking : startWorking}
    />,
  );

  return (
    <div className="space-y-5">
      {header}
      <QueueBuckets summary={summary} filters={filters} onState={(state) => changeFilters({ state })} />
      <QueueToolbar
        filters={filters} summary={summary} total={data?.total} search={search}
        onFilters={changeFilters} onSearch={setSearch}
      />

      {toast && <SavedToast text={toast} />}
      {error && <Alert variant="error"><AlertDescription>{error}</AlertDescription></Alert>}
      {loading && !data && <div className="flex justify-center py-8"><Spinner size="lg" /></div>}

      {data && (
        <div className="relative space-y-3">
          {loading && (
            <div className="absolute inset-0 z-10 flex items-start justify-center rounded-2xl bg-black/30 pt-8"><Spinner /></div>
          )}
          {rows.length === 0 ? (
            <div className="rounded-2xl border border-white/10 bg-surface px-5 py-12 text-center text-ink-dim">
              Nothing here — this bucket is clear.
            </div>
          ) : (
            <QueueTable
              rows={rows} rankOffset={(filters.page - 1) * PAGE_SIZE} metric={filters.metric} sort={filters.sort}
              openId={openId} working={working} onLog={onLog} onCancel={stopWorking} onSaved={onSaved}
            />
          )}
          <Pager page={filters.page} total={data.total} onPage={(page) => changeFilters({ page })} />
        </div>
      )}

      <p className="text-xs text-ink-dim">
        Order: overdue follow-ups first, then never-contacted, each sorted by the chosen NGR (30d or total), highest first.
        Activity tiers use days since the last bet, login, deposit or bonus, per the &ldquo;Activity by&rdquo; switch:
        Active under 7d · Quiet 7–13d · Quiet 14–29d · Inactive 30d+ · No activity on record.
      </p>
    </div>
  );
}
